<script setup lang="ts">
import { ref, computed, nextTick } from 'vue';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { EyeIcon } from '@heroicons/vue/24/solid';

const { 
  src, 
  alt = '', 
  class: className = '', 
  width, 
  height, 
  previewWidth,
  fallbackSrc = '/no-image.svg'
} = defineProps<{
  src: string | null;
  alt?: string;
  width?: string | number;
  height?: string | number;
  previewWidth?: string | number;
  class?: string;
  fallbackSrc?: string;
}>();

const isHovered = ref(false);
const isPopoverOpen = ref(false);
const triggerRef = ref<HTMLElement>();
const imageError = ref(false);

/**
 * Has the zoom Popover been mounted yet?
 *
 * Until the first hover (or a programmatic {@link openPreview}), this component
 * renders a plain `<img>` and nothing else. Mounting the reka-ui Popover stack
 * per image is what made long image lists lock the page up: a 500-track bucket
 * or a full play queue put 500 Popover roots on screen at once, and switching
 * track-card density tore all of them down and rebuilt them in one synchronous
 * pass. Images the user never hovers now cost an `<img>` and no more.
 *
 * Same gate, same reason, as {@link useLazyOverflowTooltip} — see its docblock,
 * which describes the table-cell version of this exact freeze.
 */
const activated = ref(false);


const imageStyle = computed(() => ({
  width: width ? `${width}px` : 'auto',
  height: height ? `${height}px` : 'auto',
}));

const previewImageStyle = computed(() => ({
  width: previewWidth ? `${previewWidth}px` : 'auto',
  maxWidth: '80vw',
  maxHeight: '80vh',
}));

const actualSrc = computed(() => {
  if (!src || imageError.value) {
    return fallbackSrc;
  }
  return src;
});

const handleImageError = () => {
  imageError.value = true;
};

const popoverPosition = computed(() => {
  if (!triggerRef.value) return { side: 'bottom' as const, align: 'center' as const };
  
  const rect = triggerRef.value.getBoundingClientRect();
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  
  const spaceAbove = rect.top;
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceLeft = rect.left;
  const spaceRight = viewportWidth - rect.right;
  
  let side: 'top' | 'bottom' | 'left' | 'right' = 'bottom';
  if (spaceAbove > spaceBelow && spaceAbove > 200) {
    side = 'top';
  } else if (spaceBelow > spaceAbove && spaceBelow > 200) {
    side = 'bottom';
  } else if (spaceRight > spaceLeft && spaceRight > 300) {
    side = 'right';
  } else if (spaceLeft > spaceRight && spaceLeft > 300) {
    side = 'left';
  }
  
  // Determine alignment
  let align: 'start' | 'center' | 'end' = 'center';
  if (side === 'top' || side === 'bottom') {
    if (spaceLeft < 100) align = 'start';
    else if (spaceRight < 100) align = 'end';
  } else {
    if (spaceAbove < 100) align = 'start';
    else if (spaceBelow < 100) align = 'end';
  }
  
  return { side, align } as const;
});

/**
 * First hover: mount the Popover and carry the in-progress hover across, since
 * the freshly-mounted trigger gets no `pointerenter` of its own.
 */
const activate = () => {
  isHovered.value = true;
  activated.value = true;
};

/**
 * The pointer left.
 *
 * Bound to the PRE-activation tile as well as the mounted trigger, which is the
 * whole point: `activate()` carries the in-progress hover across the branch
 * swap, but the new node only receives `mouseleave` if the pointer is still over
 * it. Sweeping across a grid of covers left every tile you brushed showing its
 * dark scrim and eye icon until you went back and hovered it properly — nothing
 * carried the END of the hover across.
 */
const releaseHover = () => {
  isHovered.value = false;
};

/** Programmatic open for consumers whose trigger surface is covered (e.g. a play overlay). */
const openPreview = () => {
  if (!activated.value) {
    activated.value = true;
    // The Popover has to exist before it can be opened.
    void nextTick(() => {
      isPopoverOpen.value = true;
    });

    return;
  }

  isPopoverOpen.value = true;
};

defineExpose({ openPreview });

// Outside-click / escape dismissal is handled by the Popover primitive's DismissableLayer; no
// manual document listener is needed (the previous one leaked on unmount and also closed the
// popover when its own content was clicked, since it only checked the trigger element).
</script>

<template>
  <!--
    Pre-activation: the tile and nothing else. `@click` is here as well as on the
    Popover trigger below because a touch tap fires `pointerenter` and `click` in
    the same gesture — the re-render that mounts the Popover lands between them,
    so without this the first tap on a touch device would do nothing.
  -->
  <div
    v-if="!activated"
    ref="triggerRef"
    class="relative inline-block cursor-pointer group"
    :class="className"
    :style="imageStyle"
    @pointerenter="activate"
    @pointerleave="releaseHover"
    @focusin="activate"
    @click="openPreview"
  >
    <img
      :src="actualSrc"
      :alt="alt"
      class="w-full h-full object-cover"
      :style="imageStyle"
      loading="lazy"
      decoding="async"
      @error="handleImageError"
    />
  </div>
  <Popover v-else v-model:open="isPopoverOpen">
    <PopoverTrigger asChild>
      <div 
        ref="triggerRef"
        class="relative inline-block cursor-pointer group"
        :class="className"
        :style="imageStyle"
        @mouseenter="isHovered = true"
        @mouseleave="releaseHover"
      >
        <img
          :src="actualSrc"
          :alt="alt"
          class="w-full h-full object-cover"
          :style="imageStyle"
          loading="lazy"
          decoding="async"
          @error="handleImageError"
        />
        <div 
          v-show="isHovered"
          class="absolute inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center transition-opacity duration-200"
        >
          <EyeIcon class="w-6 h-6 text-white drop-shadow-lg" />
        </div>
      </div>
    </PopoverTrigger>
    
    <PopoverContent 
      :side="popoverPosition.side"
      :align="popoverPosition.align"
      :side-offset="8"
      class="p-0 border-0 bg-transparent shadow-none max-w-none w-auto h-auto"
    >
      <div class="elevated-surface relative rounded-3xl p-2">
        <img 
          :src="actualSrc" 
          :alt="alt"
          class="rounded"
          :style="previewImageStyle"
        />
      </div>
    </PopoverContent>
  </Popover>
</template> 